import { apiFetch, buildQuery } from "./client";

/** Sprint 4 contract: docs/api/sprint4-sabloni-prilozi-komentari.md §3. */

export const ATTACHMENT_ENTITY_KINDS = ["ASSET_TYPE", "ASSET", "POSITION", "LOCATION", "TEMPLATE"] as const;
export type AttachmentEntityKind = (typeof ATTACHMENT_ENTITY_KINDS)[number];

export const ATTACHMENT_KINDS = ["DRAWING", "PHOTO", "MANUAL", "CERTIFICATE", "OTHER", "LAYOUT"] as const;
export type AttachmentKind = (typeof ATTACHMENT_KINDS)[number];
/** Kinds a user picks in the attachments panel — LAYOUT is only ever created through "Novi layout". */
export const UPLOAD_KINDS: AttachmentKind[] = ["DRAWING", "PHOTO", "MANUAL", "CERTIFICATE", "OTHER"];
export const LAYOUT_BACKGROUND_KIND: AttachmentKind = "LAYOUT";

/** Serbian labels; rendered via t("attachments.kind.<KIND>", fallback). */
export const ATTACHMENT_KIND_LABEL: Record<AttachmentKind, string> = {
  DRAWING: "Crtež",
  PHOTO: "Fotografija",
  MANUAL: "Uputstvo",
  CERTIFICATE: "Sertifikat/atest",
  OTHER: "Ostalo",
  LAYOUT: "Podloga layouta",
};

export type AttachmentStatus = "PENDING" | "READY";

export type AttachmentView = {
  id: string;
  entityKind: AttachmentEntityKind;
  entityId: string;
  /** `<entity code>-P<n>`, assigned on confirm. */
  code: string;
  kind: AttachmentKind;
  filename: string;
  contentType: string;
  sizeBytes: number;
  status: AttachmentStatus;
  uploadedByName: string;
  createdAt: string;
  /** Presigned GET URLs (15 min); null while PENDING. */
  downloadUrl: string | null;
  inlineUrl: string | null;
  thumbnailUrl: string | null;
};

export type PresignRequest = {
  entityKind: AttachmentEntityKind;
  entityId: string;
  kind: AttachmentKind;
  filename: string;
  contentType: string;
  sizeBytes: number;
};

export type PresignResponse = {
  attachmentId: string;
  uploadUrl: string;
  objectKey: string;
  expiresInSeconds: number;
};

export type AttachmentListParams = {
  entityKind: AttachmentEntityKind;
  entityId: string;
  kind?: AttachmentKind;
};

/** Server limit (§3): 50 MB per file. Any file type is accepted (2026-09-22); checked client-side before presign too. */
export const MAX_ATTACHMENT_BYTES = 52_428_800;

/** Browsers report "" for extensions they do not know (.dwg, .step…); the server stores those as a generic binary. */
export const DEFAULT_ATTACHMENT_CONTENT_TYPE = "application/octet-stream";

export type AttachmentFileProblem = "TOO_LARGE";

/** Client-side pre-flight of a picked file; null = OK to presign. */
export function validateAttachmentFile(file: { type: string; size: number }): AttachmentFileProblem | null {
  if (file.size > MAX_ATTACHMENT_BYTES) return "TOO_LARGE";
  return null;
}

/** glTF binary — a layout's 3D model (sprint 6 §9). Browsers report "" for .glb, so the name decides. */
export const MODEL_CONTENT_TYPE = "model/gltf-binary";

export function attachmentContentType(file: { type: string; name?: string }): string {
  if (file.type && file.type !== DEFAULT_ATTACHMENT_CONTENT_TYPE) return file.type;
  if (file.name && /\.glb$/i.test(file.name)) return MODEL_CONTENT_TYPE;
  return file.type || DEFAULT_ATTACHMENT_CONTENT_TYPE;
}

export function isImageContentType(contentType: string): boolean {
  return contentType.startsWith("image/");
}

/** A stored GLB — by its type, or by its name when it was uploaded before the type was known. */
export function isModelAttachment(a: { contentType: string; filename: string }): boolean {
  return a.contentType === MODEL_CONTENT_TYPE || /\.glb$/i.test(a.filename);
}

export const attachmentsApi = {
  /** Only READY rows, `createdAt DESC`. */
  list: (params: AttachmentListParams) =>
    apiFetch<AttachmentView[]>(`/attachments${buildQuery(params)}`),
  /** 201; creates a PENDING row. 400 VALIDATION (type/size), 404 unknown entity. */
  presign: (body: PresignRequest) =>
    apiFetch<PresignResponse>("/attachments/presign", { method: "POST", body }),
  /** Verifies the object + magic bytes, builds the thumbnail, assigns `code`. 400 UPLOAD_INVALID. */
  confirm: (id: string) =>
    apiFetch<AttachmentView>(`/attachments/${id}/confirm`, { method: "POST" }),
  /** 204 (soft delete). */
  remove: (id: string) => apiFetch<void>(`/attachments/${id}`, { method: "DELETE" }),
};

/**
 * Raw PUT of the file bytes to the presigned URL (MinIO). Deliberately NOT
 * `apiFetch`: no `/api` prefix, no cookies (the URL is self-authorising) and
 * the body is the file itself. The Content-Type must match what was presigned.
 */
export async function uploadToUrl(file: File, url: string, contentType: string = attachmentContentType(file)): Promise<void> {
  const res = await fetch(url, {
    method: "PUT",
    body: file,
    credentials: "omit",
    headers: { "Content-Type": contentType },
  });
  if (!res.ok) {
    throw new Error(`Upload failed (${res.status})`);
  }
}
